import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Edit, Trash2, Plus } from "lucide-react"; // افترض أن lucide-react مثبت؛ إذا لم يكن، استبدل بـ text

const VariantSection = ({
  variants,
  attributes,
  onAddVariant,
  onDeleteVariant,
  onUpdateVariant,
}) => {
  const { t } = useTranslation();
  const [newVariant, setNewVariant] = useState({
    quantity: "",
    values: [],
  });

  const [selectedValues, setSelectedValues] = useState({});

  // state للتعديل (الكمية فقط)
  const [editingVariant, setEditingVariant] = useState(null); // index اللي قيد التعديل
  const [editVariant, setEditVariant] = useState({
    quantity: "",
    values: [],
  });

  const handleAddVariant = () => {
    if (
      newVariant.quantity &&
      Object.values(selectedValues).length === attributes.length
    ) {
      onAddVariant({
        ...newVariant,
        values: Object.values(selectedValues),
      });
      setNewVariant({
        quantity: "",
        values: [],
      });
      setSelectedValues({});
    }
  };

  const handleValueSelect = (attrIndex, value) => {
    setSelectedValues((prev) => ({
      ...prev,
      [attrIndex]: value,
    }));
  };

  // وظائف للتعديل والحذف
  const handleEditVariant = (index) => {
    setEditingVariant(index);
    setEditVariant({ ...variants[index] }); // نسخ الكمية والقيم كما هي
  };

  const handleDeleteVariant = (index) => {
    console.log("Deleting variant at index:", index); // للاختبار
    if (onDeleteVariant) {
      onDeleteVariant(index); // يحذف العنصر من المصفوفة في المكون الأب
    } else {
      console.error("onDeleteVariant callback not provided!");
    }
  };

  const handleSaveEdit = () => {
    if (onUpdateVariant && editingVariant !== null && editVariant.quantity) {
      console.log(
        "Saving edit for variant at index:",
        editingVariant,
        "with quantity:",
        editVariant.quantity
      ); // للاختبار
      onUpdateVariant(editingVariant, {
        ...editVariant, // يحدث الكمية فقط، القيم تبقى كما هي
      });
      setEditingVariant(null);
      setEditVariant({ quantity: "", values: [] });
    } else {
      console.error("onUpdateVariant callback not provided or invalid data!");
    }
  };

  const handleCancelEdit = () => {
    setEditingVariant(null);
    setEditVariant({ quantity: "", values: [] });
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">
        {t("variants_title")}
      </h2>

      {attributes.length === 0 ? (
        <p className="text-gray-500 text-center py-4">
          {t("add_attributes_first")}
        </p>
      ) : (
        <div className="space-y-6">
          {/* قسم إضافة variant جديد */}
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              إضافة خيار جديد
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  {t("quantity_label")}
                </label>
                <input
                  type="number"
                  value={newVariant.quantity}
                  onChange={(e) =>
                    setNewVariant((prev) => ({
                      ...prev,
                      quantity: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="أدخل الكمية"
                />
              </div>
            </div>

            {/* اختيار القيم لكل attribute */}
            <div className="mt-4 space-y-3">
              {attributes?.map((attr, attrIndex) => (
                <div key={attrIndex} className="flex flex-col">
                  <label className="text-sm font-medium mb-1 text-gray-700">
                    {attr?.translations?.ar?.name || "Attribute"}
                  </label>
                  <select
                    value={selectedValues[attrIndex] || ""}
                    onChange={(e) => {
                      handleValueSelect(attrIndex, e.target.value);
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t("select_value")}</option>
                    {attr?.values?.map((val, valueIndex) => (
                      <option
                        key={valueIndex}
                        value={val?.translations?.ar?.value}
                      >
                        {val?.translations?.ar?.value}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={handleAddVariant}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center gap-2"
              disabled={
                !newVariant.quantity ||
                Object.values(selectedValues).length !== attributes?.length
              }
            >
              <Plus size={16} />
              إضافة الخيار
            </button>
          </div>
        </div>
      )}

      {/* قسم عرض الـ variants */}
      {variants?.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">
            {t("added_variants_title")}
          </h3>
          <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
            <table className="min-w-full bg-white border-collapse">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-3 px-4 border-b border-gray-200 font-semibold text-gray-700">
                    القيم
                  </th>
                  <th className="py-3 px-4 border-b border-gray-200 font-semibold text-gray-700">
                    الكمية
                  </th>
                  <th className="py-3 px-4 border-b border-gray-200 font-semibold text-gray-700">
                    الإجراءات
                  </th>
                </tr>
              </thead>
              <tbody>
                {variants?.map((variant, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="py-3 px-4 text-center border-b border-gray-200 text-gray-800">
                      {variant.values?.map((value, i) => (
                        <span
                          key={i}
                          className="text-center inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded mr-1"
                        >
                          {value}
                        </span>
                      ))}
                    </td>
                    <td className="text-center py-3 px-4 border-b border-gray-200 text-gray-800 font-medium">
                      {editingVariant === index ? (
                        <input
                          type="number"
                          value={editVariant.quantity}
                          onChange={(e) =>
                            setEditVariant((prev) => ({
                              ...prev,
                              quantity: e.target.value,
                            }))
                          }
                          className="text-center w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        variant?.quantity
                      )}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-200 text-center">
                      {editingVariant === index ? (
                        <div className="flex justify-center gap-2">
                          <button
                            type="button"
                            onClick={handleSaveEdit}
                            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1"
                          >
                            حفظ
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelEdit}
                            className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center gap-1"
                          >
                            إلغاء
                          </button>
                        </div>
                      ) : (
                        <div className="flex justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleEditVariant(index)}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
                          >
                            <Edit size={14} />
                            تعديل
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteVariant(index)}
                            className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-1"
                          >
                            <Trash2 size={14} />
                            حذف
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default VariantSection;
